import React from 'react';
import { useTranslation } from 'localization';
import type { GlyphColor } from '../../../../types/glyph';
import SphereAvatar from '../../../atoms/sphere-avatar/sphere-avatar';
import './RosterMemberIdentity.scss';

/** One constant mark for a member the caller cannot see; derived from no member field so it discloses nothing. */
const HIDDEN_MEMBER_GLYPH = 'lucide:eye-off';

/** Mark for a visible member whose type carries no glyph; the same neutral circle the graph service defaults an unknown type to. */
const UNKNOWN_TYPE_GLYPH = 'lucide:circle';

export interface RosterMemberIdentityProps {
  hidden: boolean;
  name: string | null;
  glyph?: string | null;
  glyphColor?: GlyphColor | null;
}

/**
 * The read-only part of a member: the type avatar beside the sphere's name, or the neutral
 * placeholder when the caller cannot see it. No contribution line and no editing controls, so
 * the roster's MemberCell and the rollup summary's member card draw one identity.
 */
const RosterMemberIdentity: React.FC<RosterMemberIdentityProps> = ({ hidden, name, glyph, glyphColor }) => {
  const { t } = useTranslation();
  const label = (hidden ? null : name) ?? t('statusFactors.roster.unnamed', 'Hidden sphere');
  return (
    <span className="rollup-roster__member-head">
      <span className="rollup-roster__member-avatar" aria-hidden="true" data-roster-avatar data-testid="roster-member-avatar">
        <SphereAvatar
          glyph={hidden ? HIDDEN_MEMBER_GLYPH : (glyph ?? UNKNOWN_TYPE_GLYPH)}
          glyphColor={hidden ? undefined : (glyphColor ?? undefined)}
          size="small"
        />
      </span>
      <span className="rollup-roster__member" data-testid="roster-cell-member" title={label}>
        {label}
      </span>
    </span>
  );
};

export default RosterMemberIdentity;
